import { useNavigate } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from '@apps/web/components/ui/button';
import { Skeleton } from '@apps/web/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@apps/web/components/ui/tabs';
import { buildAgentNoteChip } from '@apps/web/features/admin_agent_dock/chip_context_providers';
import { useRouteChipPublisher } from '@apps/web/features/admin_agent_dock/route_chip_source';
import { ANALYTICS_TIMEZONE } from '@apps/web/features/admin_agent_notes/model/constants';
import { buildNoteLinks } from '@apps/web/features/admin_agent_notes/model/note_trace_links';
import { useAgentNoteDetail } from '@apps/web/features/admin_agent_notes/model/use_agent_note_detail';
import {
    NoteDetailContent,
    NoteTraceActions,
} from '@apps/web/features/admin_agent_notes/ui/note_detail_body';
import { STATUS_LABELS } from '@apps/web/features/admin_agent_notes/ui/note_status';
import { NotesFilterBar } from '@apps/web/features/admin_agent_notes/ui/notes_filter_bar';
import { SourceBadge } from '@apps/web/features/admin_agent_notes/ui/source_icon';
import { formatDateAndTime } from '@apps/web/lib/date_utils';

/**
 * Standalone note page: same header and filters as the feed, with
 * prev/next stepping through the filtered list — no round trip back to
 * the feed to open the next note.
 */
export function NoteDetailPage() {
    const detail = useAgentNoteDetail();
    const navigate = useNavigate();
    const note = detail.note;

    // Chip source: upgrade the dock's route auto-chip (fallback
    // `Note <short-id>` from the registry resolver) with the org name
    // and the structured facts block as soon as the note row lands.
    useRouteChipPublisher(
        `/admin/agent-notes/${detail.noteId}`,
        note ? buildAgentNoteChip(note) : null,
    );

    return (
        <div className="flex flex-1 flex-col gap-4 p-6">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h1 className="text-xl font-semibold">Agent notes</h1>
                    <p className="text-muted-foreground text-sm">
                        What agents capture, how each note classifies and
                        routes, and the trace behind it.
                    </p>
                </div>
                <Tabs
                    value="notes"
                    onValueChange={(value) => {
                        void navigate({
                            to: '/admin/agent-notes',
                            search: {
                                ...detail.search,
                                tab:
                                    value === 'dashboard'
                                        ? 'dashboard'
                                        : 'notes',
                                note: undefined,
                            },
                        });
                    }}
                >
                    <TabsList>
                        <TabsTrigger value="notes">Notes</TabsTrigger>
                        <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
                    </TabsList>
                </Tabs>
            </div>
            <NotesFilterBar
                search={detail.search}
                statusCounts={detail.statusCounts}
                onPatch={detail.patchSearch}
            />
            {detail.hasError && (
                <p className="text-destructive rounded-md border px-3 py-6 text-center text-sm">
                    Note failed to load. It may not exist.
                </p>
            )}
            {note === null && !detail.hasError && (
                <Skeleton className="h-[420px]" />
            )}
            {note && (
                <div className="bg-card flex flex-col rounded-lg border">
                    <div className="flex flex-col gap-2 border-b px-5 py-4">
                        <div className="flex items-start justify-between gap-2">
                            <span className="flex items-center gap-2 text-base font-semibold">
                                {STATUS_LABELS[note.status]} ·{' '}
                                {note.organization_name}
                            </span>
                            <span className="flex items-center gap-0.5">
                                {detail.canRelease && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="mr-1 h-7 px-2 text-xs"
                                        disabled={detail.isReleasing}
                                        onClick={() => {
                                            void detail.releaseNote();
                                        }}
                                    >
                                        {detail.isReleasing
                                            ? 'Releasing…'
                                            : 'Release note'}
                                    </Button>
                                )}
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    aria-label="Previous note"
                                    disabled={!detail.hasPreviousNote}
                                    onClick={() => {
                                        detail.navigateNote('previous');
                                    }}
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7"
                                    aria-label="Next note"
                                    disabled={!detail.hasNextNote}
                                    onClick={() => {
                                        detail.navigateNote('next');
                                    }}
                                >
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                            </span>
                        </div>
                        <p className="text-muted-foreground flex flex-wrap items-center gap-1.5 text-xs">
                            <SourceBadge
                                source={note.source}
                                rating={note.links.source_rating}
                            />
                            {' · '}
                            {formatDateAndTime(
                                note.created_at,
                                ANALYTICS_TIMEZONE,
                            )}
                            {note.created_by_user_name
                                ? ` · by ${note.created_by_user_name}`
                                : ''}
                        </p>
                        <NoteTraceActions
                            note={note}
                            links={buildNoteLinks(note)}
                            isRetrying={detail.isRetrying}
                            onRetry={(id) => {
                                void detail.retryClassification(id);
                            }}
                        />
                    </div>
                    <div className="px-5 py-4">
                        <NoteDetailContent
                            key={note.id}
                            note={note}
                            isRetrying={detail.isRetrying}
                            onRetryWithContent={(id, content) =>
                                detail.retryClassification(id, content)
                            }
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
